import { Link } from 'react-router';
import { formatMillioSav, telefonMegjelenites } from '../lib/formaz.ts';
import { TIPUS_CIMKE, type IngatlanTipus, type LeadStatusz } from '../lib/tipusok.ts';
import { StatuszJelveny } from '../ui/elemek.tsx';
import ArSav from '../ui/ArSav.tsx';
import SlaCsik from '../ui/SlaCsik.tsx';
import { slaAllapot } from '../ui/slaSzamitas.ts';
import { IDOSAV_KOSZONO, type Idosav } from '../../../web/src/lib/cimkek.ts';
import { esedekes, honapCimke } from './ujrahivas.ts';

export interface LeadSor {
  id: string; office_id: string; created_at: string; telefon_ellenorizve_at: string | null; statusz: LeadStatusz; ingatlan_tipus: IngatlanTipus;
  iranyitoszam: string; utca: string | null; hazszam: string | null; nev: string; telefon: string;
  pontszam: number; becsles_min: number | null; becsles_max: number | null; becsles_kozep: number | null; szakertoi_becsles: boolean | null;
  hivasi_idosav: string | null; assigned_to: string | null; ujrahivas_honap: string | null;
  /** A becslés-bemenet alapterülete (m²), szövegként a JSON-útvonalról; hiányozhat. */
  alapterulet: string | null;
  terulet: { nev: string } | null; kollega: { nev: string } | null; iroda: { nev: string } | null;
}

/** A sáv, ha van szám is: alsó, felső, közép (Ft). Szakértői becslésnél vagy hiányzó adatnál null. */
export function savAdat(l: LeadSor): { min: number; max: number; kozep: number } | null {
  if (l.szakertoi_becsles || l.becsles_min === null || l.becsles_max === null) return null;
  return { min: l.becsles_min, max: l.becsles_max, kozep: l.becsles_kozep ?? (l.becsles_min + l.becsles_max) / 2 };
}

/** Az ingatlan első sora: „Lakás, 62 m²” (az alapterület csak ha ismert). */
export function ingatlanSzoveg(l: LeadSor): string {
  const m2 = Number(l.alapterulet);
  return Number.isFinite(m2) && m2 > 0 ? `${TIPUS_CIMKE[l.ingatlan_tipus]}, ${m2} m²` : TIPUS_CIMKE[l.ingatlan_tipus];
}

export function helySzoveg(l: LeadSor): string {
  return [l.terulet?.nev ?? l.iranyitoszam, [l.utca, l.hazszam].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

/** „+36 30 412 8890, délután hívható” / „…, bármikor”. */
export function elerhetoSzoveg(l: LeadSor): string {
  const tel = telefonMegjelenites(l.telefon);
  const idosav = l.hivasi_idosav ? IDOSAV_KOSZONO[l.hivasi_idosav as Idosav] : undefined;
  if (!idosav) return tel;
  return l.hivasi_idosav === 'barmikor' ? `${tel}, ${idosav}` : `${tel}, ${idosav} hívható`;
}

/** Az ársáv felirata: a szám vastagon, az egység halkan. */
function savFelirat(min: number, max: number) {
  const [szam, ...egyseg] = formatMillioSav(min, max).split(' ');
  return <><b>{szam}</b> {egyseg.join(' ')}</>;
}

export function HivasGomb({ telefon, className = '' }: { telefon: string; className?: string }) {
  return (
    <a href={`tel:${telefon}`} aria-label={`Hívás: ${telefonMegjelenites(telefon)}`}
      className={`inline-flex min-h-10 items-center justify-center rounded-[10px] bg-kiemelt-halvany px-4 text-sm font-semibold text-kiemelt transition hover:bg-felulet-lista ${className}`}>
      Hívás
    </a>
  );
}


function Pont({ pont }: { pont: number }) {
  return <span className="szam text-xs text-szoveg-halk" title="Lead pontszám">{pont} pont</span>;
}

/** Főkönyv-sor: asztalon hat oszlop, mobilon a név, az ingatlan, a sáv és az állapot egymás alatt. */
export default function LeadFokonyvSor({ lead: l, kozosSkala, slaPerc, nowMs, irodaNevMutat }: {
  lead: LeadSor; kozosSkala: { skalaMin: number; skalaMax: number } | null; slaPerc: number | undefined; nowMs: number; irodaNevMutat: boolean;
}) {
  const sav = savAdat(l);
  const sla = l.statusz === 'uj' && slaPerc ? slaAllapot({ kezdetMs: Date.parse(l.telefon_ellenorizve_at ?? l.created_at), slaPerc, nowMs }) : null;
  const jelSzin = sla?.kesik ? 'bg-masodlagos' : l.statusz === 'uj' ? 'bg-kiemelt' : 'bg-transparent';
  return (
    <li data-testid="lead-sor"
      className={`grid grid-cols-[4px_minmax(0,1fr)_auto] gap-x-3.5 gap-y-1.5 py-3.5 pr-3.5 min-[1200px]:grid-cols-[4px_minmax(190px,1.3fr)_minmax(170px,1fr)_minmax(200px,1.2fr)_150px_120px] min-[1200px]:items-center min-[1200px]:gap-x-[18px] min-[1200px]:py-4 min-[1200px]:pr-5`}>
      <span aria-hidden className={`row-span-4 self-stretch rounded-r-[3px] min-[1200px]:row-span-1 ${jelSzin}`} />

      <div className="col-start-2 min-w-0">
        <Link to={`/leadek/${l.id}`} className="block break-words text-[15px] font-medium tracking-[-0.01em] hover:text-kiemelt">{l.nev}</Link>
        <small className="mt-0.5 block text-[13px] text-szoveg-halk">{elerhetoSzoveg(l)}</small>
      </div>

      <div className={`col-start-2 min-w-0 text-sm min-[1200px]:col-auto`}>
        {ingatlanSzoveg(l)}
        <small className="mt-0.5 block text-[13px] text-szoveg-halk">{helySzoveg(l)}</small>
        {irodaNevMutat && l.iroda && <small className="mt-0.5 block text-[13px] text-szoveg-halk">{l.iroda.nev}</small>}
      </div>

      <div className={`col-start-2 min-w-0 min-[1200px]:col-auto`}>
        {sav && kozosSkala
          ? <ArSav {...sav} {...kozosSkala} felirat={savFelirat(sav.min, sav.max)} />
          : <ArSav szakerto felirat="Szakértői becslés kell" />}
      </div>

      <div className={`col-start-2 grid min-w-0 gap-1.5 text-[13px] min-[1200px]:col-auto`}>
        <span className="flex items-center gap-x-3">
          <StatuszJelveny statusz={l.statusz} />
          {sla && <Pont pont={l.pontszam} />}
        </span>
        {l.statusz === 'kesobb_aktualis' && l.ujrahivas_honap && (
          <span className={`text-xs ${esedekes(l.ujrahivas_honap, nowMs) ? 'font-medium text-kiemelt' : 'text-szoveg-halk'}`}>
            Újrahívás: {honapCimke(l.ujrahivas_honap)}
          </span>
        )}
        {sla && slaPerc
          ? <SlaCsik kezdet={l.telefon_ellenorizve_at ?? l.created_at} slaPerc={slaPerc} nowMs={nowMs} />
          : (
            <span className="flex flex-wrap gap-x-3 text-xs text-szoveg-halk">
              <span>{l.kollega?.nev ?? 'Nincs felelős'}</span>
              <Pont pont={l.pontszam} />
            </span>
          )}
      </div>

      <div className={`col-start-3 row-span-2 row-start-1 self-start min-[1200px]:col-auto min-[1200px]:row-auto min-[1200px]:row-span-1 min-[1200px]:self-center min-[1200px]:justify-self-end`}>
        <HivasGomb telefon={l.telefon} />
      </div>
    </li>
  );
}
